import { useState } from 'react';
import {
  NEEDS,
  WEAR_PART_LABELS,
  formatPence,
  priceBand,
  productsOf,
  shelfRulesOf,
  type BusinessAction,
  type BusinessContent,
  type BusinessView,
  type NeedKey,
} from '@london-life/contracts';

// what a product does, in a few words ("Hunger +70 · Fun +8")
export const effectsText = (effects: Record<string, number>) =>
  Object.entries(effects)
    .filter(([, value]) => value)
    .map(([need, value]) => `${NEEDS[need as NeedKey]?.label ?? need} ${value > 0 ? '+' : ''}${value}`)
    .join(' · ');
// the quick prices an owner picks from, as Lagos Life's (0.8× to 2.5× of the usual price, kept in the band)
const PICKS = [0.8, 1, 1.25, 1.5, 2, 2.5];
const nice = (pence: number) => (pence < 1_000 ? Math.round(pence / 10) * 10 : Math.round(pence / 50) * 50);

// The shelf for other players (as Lagos Life's player shops): what is on it and at what price, stocking it from the
// till or the wallet into the storeroom, and opening it to players. Every change is saved at once.
export function ShelfPanel({
  business,
  content,
  walletPence,
  busy,
  onAct,
}: {
  business: BusinessView;
  content: BusinessContent;
  walletPence: number;
  busy: boolean;
  onAct: (body: BusinessAction, money: boolean) => Promise<boolean>;
}) {
  const shelf = business.shelf!;
  const rules = shelfRulesOf(content);
  const [from, setFrom] = useState<'till' | 'wallet'>('till');
  const [adding, setAdding] = useState(false);
  const products = productsOf(content).filter((item) => item.kinds.includes(business.kind) && item.visible);
  const listed = shelf.items.filter((item) => item.listed);
  const stored = shelf.items.filter((item) => !item.listed && item.stock > 0);
  const room = Math.max(0, shelf.room - shelf.stocked);
  const save = (items: { productId: string; pricePence: number; listed: boolean }[]) =>
    void onAct({ action: 'shelf', items }, false);
  const current = () =>
    shelf.items.map((item) => ({ productId: item.productId, pricePence: item.pricePence, listed: item.listed }));
  const balance = from === 'till' ? business.tillPence : walletPence;
  return (
    <section className="profile-card shelf-panel" aria-label="Shelf for players">
      <div className="shelf-head">
        <div>
          <strong>{shelf.open ? '🟢 Open to players' : '⚪ Closed to players'}</strong>
          <small>
            Shelf {shelf.stocked}/{shelf.room} · sold today {formatPence(shelf.soldTodayPence)}
          </small>
        </div>
        <button
          type="button"
          className={shelf.open ? 'shelf-close' : 'shelf-open'}
          disabled={busy || (!shelf.open && !listed.some((item) => item.stock > 0))}
          onClick={() => void onAct({ action: 'shelf_open', open: !shelf.open }, false)}
        >
          {shelf.open ? 'Close' : 'Open'}
        </button>
      </div>
      <div className="shelf-bar" aria-hidden="true">
        <i style={{ width: `${Math.min(100, (shelf.stocked / Math.max(1, shelf.room)) * 100)}%` }} />
      </div>
      <div className="shelf-from" role="radiogroup" aria-label="Stock from">
        <span>Stock from</span>
        {(['till', 'wallet'] as const).map((key) => (
          <button
            type="button"
            key={key}
            role="radio"
            aria-checked={from === key}
            className={from === key ? 'active' : ''}
            onClick={() => setFrom(key)}
          >
            {key === 'till' ? `Till ${formatPence(business.tillPence)}` : `Wallet ${formatPence(walletPence)}`}
          </button>
        ))}
      </div>
      <ul className="shelf-items">
        {listed.map((item) => {
          const product = products.find((entry) => entry.id === item.productId);
          if (!product) return null;
          const band = priceBand(product, rules);
          const picks = [
            ...new Set(PICKS.map((times) => Math.min(band.max, Math.max(band.min, nice(product.refPence * times))))),
          ];
          const cost = item.costPence ?? 0;
          const fill = Math.min(room, cost ? Math.floor(balance / cost) : room);
          return (
            <li key={item.productId} aria-label={product.label}>
              <div className="shelf-item-head">
                <span aria-hidden="true">{product.icon}</span>
                <div>
                  <strong>{product.label}</strong>
                  <small>
                    {item.stock} in stock · costs {formatPence(cost)}
                    {product.wear ? ` · ${WEAR_PART_LABELS[product.wear.part]}: they wear it` : ''} ·{' '}
                    {effectsText(product.effects)}
                  </small>
                </div>
                <button
                  type="button"
                  aria-label={`Take ${product.label} off the shelf`}
                  disabled={busy}
                  onClick={() =>
                    save(
                      current().map((entry) =>
                        entry.productId === item.productId ? { ...entry, listed: false } : entry,
                      ),
                    )
                  }
                >
                  ✕
                </button>
              </div>
              <strong className="shelf-price">{formatPence(item.pricePence)}</strong>
              <div className="business-chips" role="radiogroup" aria-label={`Price of ${product.label}`}>
                {picks.map((price) => (
                  <button
                    type="button"
                    key={price}
                    role="radio"
                    aria-checked={item.pricePence === price}
                    className={item.pricePence === price ? 'active' : ''}
                    disabled={busy}
                    onClick={() =>
                      save(
                        current().map((entry) =>
                          entry.productId === item.productId ? { ...entry, pricePence: price } : entry,
                        ),
                      )
                    }
                  >
                    {formatPence(price)}
                  </button>
                ))}
              </div>
              <div className="shelf-stock">
                {[10, 50].map((qty) => (
                  <button
                    type="button"
                    key={qty}
                    disabled={busy || qty > room || qty * cost > balance}
                    onClick={() => void onAct({ action: 'restock', productId: item.productId, qty, from }, true)}
                  >
                    <strong>+{qty}</strong>
                    <small>{formatPence(qty * cost)}</small>
                  </button>
                ))}
                <button
                  type="button"
                  disabled={busy || fill <= 0}
                  onClick={() => void onAct({ action: 'restock', productId: item.productId, qty: fill, from }, true)}
                >
                  <strong>Fill</strong>
                  <small>
                    {fill > 0 ? `${fill} · ${formatPence(fill * cost)}` : room ? 'Not enough money' : 'Full'}
                  </small>
                </button>
              </div>
            </li>
          );
        })}
      </ul>
      {listed.length < rules.maxListed && (
        <div className="shelf-add">
          <button type="button" className="shelf-add-open" onClick={() => setAdding(!adding)}>
            {adding ? 'Done adding' : `+ Add a product (${listed.length}/${rules.maxListed})`}
          </button>
          {adding && (
            <div className="shelf-add-list">
              {products
                .filter((product) => !listed.some((item) => item.productId === product.id))
                .map((product) => (
                  <button
                    type="button"
                    key={product.id}
                    disabled={busy}
                    onClick={() => {
                      const before = current();
                      const known = before.find((entry) => entry.productId === product.id);
                      save(
                        known
                          ? before.map((entry) => (entry.productId === product.id ? { ...entry, listed: true } : entry))
                          : [...before, { productId: product.id, pricePence: product.refPence, listed: true }],
                      );
                      if (listed.length + 1 >= rules.maxListed) setAdding(false);
                    }}
                  >
                    <span aria-hidden="true">{product.icon}</span>
                    <span>
                      <strong>{product.label}</strong>
                      <small>{formatPence(product.refPence)}</small>
                    </span>
                  </button>
                ))}
            </div>
          )}
        </div>
      )}
      {stored.length > 0 && (
        <p className="business-muted">
          In the storeroom: {stored.map((item) => `${item.icon} ${item.stock} ${item.label}`).join(', ')}
        </p>
      )}
      {!listed.length && <p className="business-muted">Add a product, stock it, then open the shelf to players.</p>}
    </section>
  );
}
